import React, { useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { Database } from '../types/supabase';
import { toast } from '../lib/toast';

type TeamMember = Database['public']['Tables']['team_members']['Row'];

interface ResourceFinderProps {
  teamMembers: TeamMember[];
  onClose: () => void;
}

export default function ResourceFinder({ teamMembers, onClose }: ResourceFinderProps) {
  const [technology, setTechnology] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<TeamMember[]>([]);
  const [searched, setSearched] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Filter team members based on technology and job title
      const matchedMembers = teamMembers.filter(member => {
        const techMatch = !technology || 
          (member.technology?.toLowerCase().includes(technology.toLowerCase()) ?? false);
        const jobMatch = !jobTitle || 
          member.job_title.toLowerCase().includes(jobTitle.toLowerCase());
        return techMatch && jobMatch;
      });

      // Sort by best match (exact matches first)
      matchedMembers.sort((a, b) => {
        const aExactTech = a.technology?.toLowerCase() === technology.toLowerCase();
        const bExactTech = b.technology?.toLowerCase() === technology.toLowerCase();
        const aExactJob = a.job_title.toLowerCase() === jobTitle.toLowerCase();
        const bExactJob = b.job_title.toLowerCase() === jobTitle.toLowerCase();
        
        if (aExactTech && !bExactTech) return -1;
        if (!aExactTech && bExactTech) return 1;
        if (aExactJob && !bExactJob) return -1;
        if (!aExactJob && bExactJob) return 1;
        return 0;
      });

      setResults(matchedMembers);
      setSearched(true);

      if (matchedMembers.length === 0) {
        toast.info('No matching team members found');
      }
    } catch (error) {
      console.error('Error finding resources:', error);
      toast.error('Failed to search for resources');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Find Resource
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="technology" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Technology
              </label>
              <input
                type="text"
                id="technology"
                value={technology}
                onChange={(e) => setTechnology(e.target.value)}
                placeholder="e.g., React, Python, Node.js"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>

            <div>
              <label htmlFor="job-title" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Job Title
              </label>
              <input
                type="text"
                id="job-title"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="e.g., Frontend Developer, Backend Engineer"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin h-5 w-5 mr-2" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="h-5 w-5 mr-2" />
                  Find Resources
                </>
              )}
            </button>
          </form>

          {searched && (
            <div className="mt-8">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Search Results {results.length > 0 && `(${results.length} found)`}
              </h3>
              <div className="space-y-4">
                {results.length > 0 ? (
                  results.map((member) => (
                    <div
                      key={member.id}
                      className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4"
                    >
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {member.name}
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {member.job_title}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {member.technology}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {member.email}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                    No matching resources found. Try adjusting your search criteria.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}